import { ApiWeatherData, ForecastData, HourlyWeatherData, ZodForecastData } from './types';
import { WeatherWidget } from './WeatherWidget';

/**
 * Weather Data
 */
export class WeatherData {
    weather: ForecastData;
    hourly: Array<HourlyWeatherData> = [];

    constructor(weatherData: ApiWeatherData, temperatureUnits: string, iconVersion: string) {
        const forecastDataConstruction: Record<string, number | string | null | undefined> = {};

        /**
         * Get the current forecast data...
         */
        weatherData.forecast.forEach((weatherData, index) => {
            let name = '';
            switch (index) {
                case 0:
                    name = 'today';
                    break;
                case 1:
                    name = 'tomorrow';
                    break;
                default:
                    name = `day${index}`;
                    break;
            }
            /**
             * Shared day items.
             */
            forecastDataConstruction[`${name}_condition`] = weatherData.condition;
            forecastDataConstruction[`${name}_hi`] = WeatherData.convertTemperature(
                weatherData.temp_high,
                temperatureUnits
            );
            forecastDataConstruction[`${name}_icon`] =
                WeatherWidget.getWeatherIcon(weatherData.icon, iconVersion) ||
                '/images/weather_icon/na.png';
            forecastDataConstruction[`${name}_low`] = WeatherData.convertTemperature(
                weatherData.temp_low,
                temperatureUnits
            );
            if (index > 0) {
                /**
                 * These values are only found on tomorrow or numbered days.
                 */
                forecastDataConstruction[`${name}_day`] = WeatherWidget.dayNameFromUnixTimestamp(
                    weatherData.timestamp
                );
                forecastDataConstruction[`${name}_feels_like`] = WeatherData.convertTemperature(
                    weatherData.feels_like,
                    temperatureUnits
                );
            }
        });

        /**
         * Get the current weather data...
         */
        const firstCurrentItem = weatherData.current[0];
        if (firstCurrentItem) {
            forecastDataConstruction.weather_locality_id = firstCurrentItem.weather_locality_id;
            forecastDataConstruction.area = firstCurrentItem.area;
            forecastDataConstruction.condition = firstCurrentItem.condition;
            forecastDataConstruction.currday_background = WeatherWidget.getWeatherBackground(
                firstCurrentItem.icon
            );
            forecastDataConstruction.currday_icon =
                WeatherWidget.getWeatherIcon(firstCurrentItem.icon, iconVersion) ||
                '/images/weather_icon/na.png';
            forecastDataConstruction.currday_temp = WeatherData.convertTemperature(
                firstCurrentItem.temperature,
                temperatureUnits
            );
            forecastDataConstruction.description = firstCurrentItem.description;
            forecastDataConstruction.feels_like = WeatherData.convertTemperature(
                firstCurrentItem.feels_like,
                temperatureUnits
            );
            forecastDataConstruction.locality = firstCurrentItem.locality;
            forecastDataConstruction.wind_direction = firstCurrentItem.wind_direction;
            forecastDataConstruction.wind_direction_icon =
                '/images/weather_icon/wind/wind_dir2.png';
            forecastDataConstruction.wind_speed = firstCurrentItem.wind_speed;
        }

        this.weather = ZodForecastData.parse(forecastDataConstruction);

        if (weatherData.hourly && weatherData.hourly.length > 0) {
            for (let i = 0; i < weatherData.hourly.length; i++) {
                const hourObject: HourlyWeatherData = {
                    day: null,
                    date: null,
                    time: null,
                    temp_high: null,
                    temp_low: null,
                    condition: null,
                    icon: null,
                    feels_like: null,
                };

                hourObject.day = WeatherWidget.dayNameFromUnixTimestamp(
                    weatherData.hourly[i].timestamp
                );
                hourObject.date = weatherData.hourly[i].date;
                hourObject.time = weatherData.hourly[i].time;
                hourObject.temp_high = WeatherData.convertTemperature(
                    weatherData.hourly[i].temp_high,
                    temperatureUnits
                );
                hourObject.temp_low = WeatherData.convertTemperature(
                    weatherData.hourly[i].temp_low,
                    temperatureUnits
                );
                hourObject.condition = weatherData.hourly[i].condition;
                hourObject.icon = WeatherWidget.getWeatherIcon(
                    weatherData.hourly[i].icon,
                    iconVersion
                );
                hourObject.feels_like = WeatherData.convertTemperature(
                    weatherData.hourly[i].feels_like,
                    temperatureUnits
                );
                this.hourly.push(hourObject);
            }
        }
    }

    /**
     * Converts temperatures in Celsius to a different temperature scale.
     * @param tempInC - temperature in Celsius
     * @param toScale - the scale to convert to (eg. fahrenheit, kelvin,
     * etc.)
     */
    static convertTemperature(tempInC: number | null, toScale: string): number | null {
        let returnTemp: number;

        if (tempInC === null) {
            return null;
        }

        switch (toScale.toString().toLowerCase()) {
            // TODO: Potentially add an enum...
            case 'fahrenheit':
            case 'f':
                returnTemp = tempInC * (9 / 5) + 32;
                break;
            case 'kelvin':
            case 'k':
                returnTemp = tempInC + 273.15;
                break;
            case 'rankine':
                returnTemp = (tempInC + 273.15) * (9 / 5);
                break;
            case 'delisle':
                returnTemp = (100 - tempInC) * 1.5;
                break;
            case 'newton':
                returnTemp = tempInC * (33 / 100);
                break;
            case 'réaumur':
                returnTemp = tempInC * 0.8;
                break;
            case 'rømer':
            case 'roemer':
                returnTemp = tempInC * (21 / 40) + 7.5;
                break;
            case 'centigrade':
            case 'celsius':
            case 'c':
            default:
                returnTemp = tempInC;
        }

        return Math.round(returnTemp);
    }
}
